import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { BackButton } from '../../components/ui/BackButton';
import { paymentsApi } from '../../api/payments';
import {
  PAYMENT_TYPE_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  PAYMENT_STATUS_ICONS,
  formatAmount,
  formatMonth,
  type PaymentType,
  type GroupPaymentSettings,
  type PaymentStatusResponse,
} from '../../types/payment';
import { athletesApi } from '../../api/athletes';

export function GroupPaymentsScreen() {
  const { getCurrentScreen, pushScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();
  const groupId = currentScreen.params?.groupId as string;
  const groupName = (currentScreen.params?.groupName as string) || 'Группа';
  const queryClient = useQueryClient();

  const [showSettingsForm, setShowSettingsForm] = useState(false);
  const [settingsForm, setSettingsForm] = useState<{
    payment_type: PaymentType;
    monthly_price: string;
    sessions_per_month: string;
    price_per_session: string;
  }>({
    payment_type: 'monthly_unlimited',
    monthly_price: '',
    sessions_per_month: '',
    price_per_session: '',
  });

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['group-payment-settings', groupId],
    queryFn: () => paymentsApi.getGroupSettings(groupId),
    enabled: !!groupId,
    retry: false,
  });

  const { data: statuses, isLoading: statusesLoading } = useQuery({
    queryKey: ['group-payment-statuses', groupId],
    queryFn: () => paymentsApi.getGroupPaymentStatuses(groupId),
    enabled: !!groupId,
  });

  const { data: athletes } = useQuery({
    queryKey: ['group-athletes', groupId],
    queryFn: () => athletesApi.getByGroupId(groupId),
    enabled: !!groupId,
  });

  const athleteNameMap = new Map((athletes || []).map((a) => [a.id, a.name]));

  const saveSettingsMutation = useMutation({
    mutationFn: async (data: typeof settingsForm) => {
      const reqData = {
        payment_type: data.payment_type,
        monthly_price: data.monthly_price || undefined,
        sessions_per_month: data.sessions_per_month ? parseInt(data.sessions_per_month) : undefined,
        price_per_session: data.price_per_session || undefined,
        currency: 'RUB',
      };
      if (settings) return paymentsApi.updateGroupSettings(groupId, reqData);
      return paymentsApi.createGroupSettings(groupId, reqData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-payment-settings', groupId] });
      queryClient.invalidateQueries({ queryKey: ['group-payment-statuses', groupId] });
      setShowSettingsForm(false);
    },
  });

  const deleteSettingsMutation = useMutation({
    mutationFn: () => paymentsApi.deleteGroupSettings(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-payment-settings', groupId] });
      queryClient.invalidateQueries({ queryKey: ['group-payment-statuses', groupId] });
    },
  });

  const handleEditSettings = (existingSettings?: GroupPaymentSettings) => {
    if (existingSettings) {
      setSettingsForm({
        payment_type: existingSettings.payment_type,
        monthly_price: existingSettings.monthly_price || '',
        sessions_per_month: existingSettings.sessions_per_month?.toString() || '',
        price_per_session: existingSettings.price_per_session || '',
      });
    }
    setShowSettingsForm(true);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid var(--separator)',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    color: 'var(--text-hint)',
    marginBottom: '4px',
  };

  if (!groupId) {
    return <div className="p-4" style={{ color: 'var(--danger)' }}>Группа не найдена</div>;
  }

  if (settingsLoading || statusesLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <div className="flex items-center gap-2 mb-4">
        <BackButton />
        <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Оплаты: {groupName}</h1>
      </div>

      {/* Payment settings */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[17px] font-semibold" style={{ color: 'var(--text-primary)' }}>Настройки оплаты</h3>
          {settings && !showSettingsForm && (
            <Button variant="secondary" onClick={() => handleEditSettings(settings)}>Изменить</Button>
          )}
        </div>

        {showSettingsForm ? (
          <div className="space-y-3">
            <div>
              <label style={labelStyle}>Тип оплаты</label>
              <select value={settingsForm.payment_type} onChange={(e) => setSettingsForm((prev) => ({ ...prev, payment_type: e.target.value as PaymentType }))} style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}>
                <option value="monthly_unlimited">Месячный абонемент</option>
                <option value="monthly_limited">Абонемент с занятиями</option>
                <option value="pay_per_session">Разовое занятие</option>
                <option value="flexible">Произвольная оплата</option>
              </select>
            </div>

            {(settingsForm.payment_type === 'monthly_unlimited' || settingsForm.payment_type === 'monthly_limited') && (
              <div>
                <label style={labelStyle}>Цена за месяц</label>
                <input type="number" value={settingsForm.monthly_price} onChange={(e) => setSettingsForm((prev) => ({ ...prev, monthly_price: e.target.value }))} placeholder="5000" style={inputStyle} />
              </div>
            )}

            {settingsForm.payment_type === 'monthly_limited' && (
              <div>
                <label style={labelStyle}>Занятий в месяц</label>
                <input type="number" value={settingsForm.sessions_per_month} onChange={(e) => setSettingsForm((prev) => ({ ...prev, sessions_per_month: e.target.value }))} placeholder="8" style={inputStyle} />
              </div>
            )}

            {settingsForm.payment_type === 'pay_per_session' && (
              <div>
                <label style={labelStyle}>Цена за занятие</label>
                <input type="number" value={settingsForm.price_per_session} onChange={(e) => setSettingsForm((prev) => ({ ...prev, price_per_session: e.target.value }))} placeholder="1000" style={inputStyle} />
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={() => saveSettingsMutation.mutate(settingsForm)} disabled={saveSettingsMutation.isPending} fullWidth>
                {saveSettingsMutation.isPending ? 'Сохранение...' : 'Сохранить'}
              </Button>
              <Button variant="secondary" onClick={() => setShowSettingsForm(false)}>Отмена</Button>
            </div>
            {saveSettingsMutation.isError && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>
                {(saveSettingsMutation.error as Error)?.message || 'Ошибка сохранения'}
              </p>
            )}
          </div>
        ) : settings ? (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>Тип:</span>
              <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{PAYMENT_TYPE_LABELS[settings.payment_type]}</span>
            </div>
            {settings.monthly_price && (
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-hint)' }}>Цена за месяц:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{formatAmount(settings.monthly_price)}</span>
              </div>
            )}
            {settings.sessions_per_month && (
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-hint)' }}>Занятий в месяц:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{settings.sessions_per_month}</span>
              </div>
            )}
            {settings.price_per_session && (
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-hint)' }}>Цена за занятие:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{formatAmount(settings.price_per_session)}</span>
              </div>
            )}
            <Button variant="secondary" onClick={() => deleteSettingsMutation.mutate()} disabled={deleteSettingsMutation.isPending} fullWidth>
              <span style={{ color: 'var(--danger)' }}>Удалить настройки</span>
            </Button>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="mb-3" style={{ color: 'var(--text-hint)' }}>Оплата для этой группы не настроена</p>
            <Button onClick={() => handleEditSettings()}>Настроить оплату</Button>
          </div>
        )}
      </Card>

      {/* Payment statuses */}
      {settings && (
        <Card>
          <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Статусы оплат</h3>
          {statuses && statuses.length > 0 ? (
            <div className="space-y-1">
              {statuses.map((status: PaymentStatusResponse) => (
                <div
                  key={status.athlete_id}
                  className="flex items-center justify-between py-2 px-2 rounded-lg cursor-pointer active:opacity-60"
                  style={{ borderBottom: '1px solid var(--separator)' }}
                  onClick={() => pushScreen('athlete-payments', { athleteId: status.athlete_id, groupId })}
                >
                  <div className="flex-1">
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {athleteNameMap.get(status.athlete_id) || 'Спортсмен'}
                    </span>
                    {status.last_paid_month && (
                      <span className="text-xs ml-2" style={{ color: 'var(--text-hint)' }}>до {formatMonth(status.last_paid_month)}</span>
                    )}
                    {status.sessions_remaining != null && status.sessions_remaining >= 0 && (
                      <span className="text-xs ml-2" style={{ color: 'var(--text-hint)' }}>({status.sessions_remaining} зан.)</span>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_STATUS_COLORS[status.status]}`}>
                    {PAYMENT_STATUS_ICONS[status.status]} {PAYMENT_STATUS_LABELS[status.status]}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-4" style={{ color: 'var(--text-hint)' }}>Нет данных об оплатах</p>
          )}
        </Card>
      )}
    </div>
  );
}
